import { privateRequest } from "../axios";

import type {
  GetUsersParams,
  GetUsersResponse,
  User,
  ApiResponse,
  ImportUserResponse,
} from "@/types/user";

const ENDPOINTS = {
  USER: "/admin/users",
  USER_ID: (id: string) => `/admin/users/${id}/customer`,
  IMPORT_USER: "/admin/users/import-user",
  DOWNLOAD_SAMPLE: "/api/file/import-files/sample-import.xlsx",
  THIRD_PARTY_SYNC: "/admin/third-party-sync",
};

const getUsers = async (params?: GetUsersParams): Promise<GetUsersResponse> => {
  const queryParams: Record<string, any> = {};
  if (params) {
    if (params.search) {
      queryParams.search = params.search;
    }
    const pageNum = params["pageNo"] ?? params.page;
    if (pageNum !== undefined) {
      queryParams["pageNo"] = pageNum;
    }
    const pageSize = params["perPage"];
    if (pageSize !== undefined) {
      queryParams["perPage"] = pageSize;
    }
  }

  const response = await privateRequest.get<any>(ENDPOINTS.USER, { params: queryParams });
  const resData = response.data;
  let rows: User[] = [];
  let count: number = 0;

  const rawPayload = resData?.data ?? resData;

  if (Array.isArray(rawPayload)) {
    rows = rawPayload;
    count =
      resData?.totalRecord ??
      resData?.totalRecords ??
      resData?.total ??
      resData?.count ??
      rawPayload.length;
  } else if (rawPayload && typeof rawPayload === "object") {
    const list =
      rawPayload.results ??
      rawPayload.rows ??
      rawPayload.users ??
      rawPayload.content ??
      rawPayload.data ??
      [];

    if (Array.isArray(list)) {
      rows = list;
    }

    count =
      rawPayload.totalRecord ??
      rawPayload.totalRecords ??
      rawPayload.totalElements ??
      rawPayload.total ??
      rawPayload.count ??
      resData?.totalRecord ??
      resData?.totalRecords ??
      resData?.count ??
      rows.length;
  }

  return {
    rows,
    count,
  };
};

const getUserById = async (id: string | number): Promise<User> => {
  try {
    const response = await privateRequest.get<any>(`/admin/users/${id}/customer`);
    console.log(response);
    return response.data?.data ?? response.data;
  } catch {
    try {
      const response = await privateRequest.get<any>(`/admin/user/${id}`);
      return response.data?.data ?? response.data;
    } catch {
      const response = await privateRequest.get<any>(`/admin/users/${id}`);
      return response.data?.data ?? response.data;
    }
  }
};

const addUser = async (data: Partial<User>): Promise<User> => {
  const response = await privateRequest.post<User>("/admin/users/customer", data);
  console.log(response);
  return response.data;
};

const updateUser = async (
  id: string | number,
  data: Partial<User>
): Promise<User> => {
  try {
    const response = await privateRequest.put<User>(`/admin/users/${id}/customer`, data);
    return response.data;
  } catch {
    const response = await privateRequest.put<User>(`/admin/users/${id}/customer`, data);
    return response.data;
  }
};

const deleteUser = async (
  id: string | number
): Promise<{ message: string }> => {
  const response = await privateRequest.delete<{ message: string }>(
    `/admin/users/${id}/customer`
  );
  return response.data;
};

const importUser = async (file: File): Promise<ImportUserResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await privateRequest.post<ImportUserResponse>(
    ENDPOINTS.IMPORT_USER,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return response.data;
};

const syncThirdParty = async (): Promise<{ message?: string }> => {
  const response = await privateRequest.post<{ message?: string }>(
    ENDPOINTS.THIRD_PARTY_SYNC
  );
  return response.data;
};

const exportSampleData = async () => {
  const response = await privateRequest.get(
    ENDPOINTS.DOWNLOAD_SAMPLE,
    {
      baseURL: import.meta.env.VITE_API_BASE_URL,
      responseType: "blob",
    }
  );
  return response;
};

const UserService = {
  getUsers,
  getUserById,
  addUser,
  updateUser,
  deleteUser,
  importUser,
  syncThirdParty,
  exportSampleData,
};

export default UserService;

export type { User };
